import {
    Component,
    ViewChild,
    Input,
    Output,
    EventEmitter,
    AfterViewInit,
    OnDestroy,
    NgZone,
    ElementRef,
} from '@angular/core';

import { iccListTab } from '../list/item';
import { MatTabGroup } from '@angular/material/tabs';

@Component({
    selector: 'icc-tabs',
    templateUrl: './tabs.component.html',
    styleUrls: ['./tabs.component.scss'],
    standalone: false
})
export class TabsComponent implements AfterViewInit, OnDestroy {
    @ViewChild('tabsGroup') tabsGroup?: MatTabGroup;
    @Input() tabs: iccListTab[] = [];
    @Input() activeTabIndex = 0;
    @Output() activated = new EventEmitter<iccListTab>();

    private tabListContainer: HTMLElement | null = null;
    private isDragging = false;
    private didDrag = false;
    private startX = 0;
    private startScrollDistance = 0;

    // Obsługa zdarzeń
    private wheelHandler = (e: WheelEvent) => this.onWheel(e);
    private mouseDownHandler = (e: MouseEvent) => this.onMouseDown(e);
    private mouseMoveHandler = (e: MouseEvent) => this.onMouseMove(e);
    private mouseUpHandler = () => this.onMouseUp();
    private touchStartHandler = (e: TouchEvent) => this.onTouchStart(e);
    private touchMoveHandler = (e: TouchEvent) => this.onTouchMove(e);
    private touchEndHandler = () => this.onTouchEnd();

    constructor(
        private ngZone: NgZone,
        private elementRef: ElementRef
    ) {}

    ngAfterViewInit() {
        setTimeout(() => {
            this.tabListContainer = this.elementRef.nativeElement.querySelector(
                '.mat-mdc-tab-label-container'
            );
            if (this.tabListContainer) {
                this.setupScrollListeners();
            }
        });
    }

    ngOnDestroy() {
        this.removeScrollListeners();
    }

    private get tabHeader(): any {
        return (this.tabsGroup as any)?._tabHeader;
    }

    private getScrollDistance(): number {
        return this.tabHeader?._scrollDistance ?? 0;
    }

    private setScrollDistanceImmediate(value: number) {
        const header = this.tabHeader;
        if (header) {
            header._scrollDistance = value;
            header._updateTabScrollPosition();
        }
    }

    private getMaxScrollDistance(): number {
        return this.tabHeader?._getMaxScrollDistance?.() ?? 0;
    }

    private setupScrollListeners() {
        if (!this.tabListContainer) return;

        this.ngZone.runOutsideAngular(() => {
            this.tabListContainer!.addEventListener('wheel', this.wheelHandler, { passive: false });
            this.tabListContainer!.addEventListener('mousedown', this.mouseDownHandler);
            document.addEventListener('mousemove', this.mouseMoveHandler);
            document.addEventListener('mouseup', this.mouseUpHandler);
            this.tabListContainer!.addEventListener('touchstart', this.touchStartHandler, {
                passive: true,
            });
            this.tabListContainer!.addEventListener('touchmove', this.touchMoveHandler, {
                passive: false,
            });
            this.tabListContainer!.addEventListener('touchend', this.touchEndHandler);
        });
    }

    private removeScrollListeners() {
        if (!this.tabListContainer) return;

        this.tabListContainer.removeEventListener('wheel', this.wheelHandler);
        this.tabListContainer.removeEventListener('mousedown', this.mouseDownHandler);
        document.removeEventListener('mousemove', this.mouseMoveHandler);
        document.removeEventListener('mouseup', this.mouseUpHandler);
        this.tabListContainer.removeEventListener('touchstart', this.touchStartHandler);
        this.tabListContainer.removeEventListener('touchmove', this.touchMoveHandler);
        this.tabListContainer.removeEventListener('touchend', this.touchEndHandler);
    }

    private onWheel(e: WheelEvent) {
        const maxScroll = this.getMaxScrollDistance();
        if (maxScroll <= 0) return;

        e.preventDefault();
        const currentScroll = this.getScrollDistance();
        const newScroll = Math.max(0, Math.min(maxScroll, currentScroll + e.deltaY));
        this.setScrollDistanceImmediate(newScroll);
    }

    private onMouseDown(e: MouseEvent) {
        const maxScroll = this.getMaxScrollDistance();
        if (maxScroll <= 0) return;

        this.isDragging = true;
        this.didDrag = false;
        this.startX = e.pageX;
        this.startScrollDistance = this.getScrollDistance();
        this.setDraggingClass(true);
        if (this.tabListContainer) {
            this.tabListContainer.style.cursor = 'grabbing';
        }
    }

    private onMouseMove(e: MouseEvent) {
        if (!this.isDragging) return;

        e.preventDefault();
        const deltaX = this.startX - e.pageX;

        if (Math.abs(deltaX) > 5) {
            this.didDrag = true;
        }

        const maxScroll = this.getMaxScrollDistance();
        const newScroll = Math.max(0, Math.min(maxScroll, this.startScrollDistance + deltaX));
        this.setScrollDistanceImmediate(newScroll);
    }

    private onMouseUp() {
        this.isDragging = false;
        this.setDraggingClass(false);
        if (this.tabListContainer) {
            this.tabListContainer.style.cursor = '';
        }
    }

    private onTouchStart(e: TouchEvent) {
        const maxScroll = this.getMaxScrollDistance();
        if (maxScroll <= 0) return;

        this.isDragging = true;
        this.didDrag = false;
        this.startX = e.touches[0].pageX;
        this.startScrollDistance = this.getScrollDistance();
        this.setDraggingClass(true);
    }

    private onTouchMove(e: TouchEvent) {
        if (!this.isDragging) return;

        const deltaX = this.startX - e.touches[0].pageX;

        if (Math.abs(deltaX) > 5) {
            this.didDrag = true;
            e.preventDefault();
        }

        const maxScroll = this.getMaxScrollDistance();
        const newScroll = Math.max(0, Math.min(maxScroll, this.startScrollDistance + deltaX));
        this.setScrollDistanceImmediate(newScroll);
    }

    private onTouchEnd() {
        this.isDragging = false;
        this.setDraggingClass(false);
    }

    private setDraggingClass(isDragging: boolean) {
        const tabGroup = this.elementRef.nativeElement.querySelector('mat-tab-group');
        if (tabGroup) {
            if (isDragging) {
                tabGroup.classList.add('dragging');
            } else {
                tabGroup.classList.remove('dragging');
            }
        }
    }

    isArrowHidden() {
        const tabBarElem = document.querySelector('.mdc-tab-scroller__scroll-area');
        if (!tabBarElem) return false;

        const w1 = tabBarElem.getBoundingClientRect().width;
        const w2 = Math.floor(
            Array.from(document.querySelectorAll('.nav-tab')).reduce(
                (prev, curr) => (prev += curr.getBoundingClientRect().width),
                0
            )
        );

        return w1 >= w2;
    }

    selectTab(tabIndex: number) {
        if (this.didDrag) {
            this.didDrag = false;
            return;
        }
        const tab = this.tabs[tabIndex];
        this.activated.emit(tab);
    }
}
